import axios from 'axios';
import { api, API_AUTH_ERROR_EVENT } from '@ohif/core';

function showSessionError(isViewerTokenError: boolean, message: string, status?: number) {
  window.dispatchEvent(
    new CustomEvent(API_AUTH_ERROR_EVENT, {
      detail: { isViewerTokenError, message, status },
    })
  );
}

function normalizeViewerToken(rawToken: string | null) {
  if (!rawToken) {
    return null;
  }

  let token = `${rawToken}`.trim();
  if (!token || token === 'undefined' || token === 'null') {
    return null;
  }

  if (token.startsWith('"') && token.endsWith('"')) {
    token = token.slice(1, -1).trim();
  }

  if (/^Bearer\s+/i.test(token)) {
    token = token.replace(/^Bearer\s+/i, '').trim();
  }

  return token || null;
}

function getViewerTokenFromStorage() {
  try {
    const tokenFromSessionStorage = sessionStorage.getItem('session_viewer');
    const normalizedFromSessionStorage = normalizeViewerToken(tokenFromSessionStorage);

    if (normalizedFromSessionStorage) {
      return {
        rawToken: tokenFromSessionStorage,
        normalizedToken: normalizedFromSessionStorage,
        source: 'sessionStorage',
      };
    }
  } catch {
    // sessionStorage access can fail in strict browser privacy contexts.
  }

  try {
    const tokenFromLocalStorage = localStorage.getItem('session_viewer');
    const normalizedFromLocalStorage = normalizeViewerToken(tokenFromLocalStorage);

    if (normalizedFromLocalStorage) {
      return {
        rawToken: tokenFromLocalStorage,
        normalizedToken: normalizedFromLocalStorage,
        source: 'localStorage',
      };
    }
  } catch {
    // localStorage access can fail in strict browser privacy contexts.
  }

  return {
    rawToken: null,
    normalizedToken: null,
    source: 'none',
  };
}
export async function checkLogin() {
  const path = '/current-user-viewer';
  const {
    rawToken: rawViewerToken,
    normalizedToken: viewer_token,
    source: viewerTokenSource,
  } = getViewerTokenFromStorage();

  const hadBearerPrefix = Boolean(rawViewerToken && /^Bearer\s+/i.test(rawViewerToken.trim()));
  const hadWrappedQuotes = Boolean(
    rawViewerToken && rawViewerToken.trim().startsWith('"') && rawViewerToken.trim().endsWith('"')
  );
  const maskedPrefix = viewer_token ? `${viewer_token.slice(0, 6)}...` : 'none';

  const cookieMatch = /(?:^|; )session_id=([^;]*)/.exec(document.cookie);
  const sessionIdFromCookie = cookieMatch?.[1] ? decodeURIComponent(cookieMatch[1]).trim() : '';
  const sessionIdFromStorage = localStorage.getItem('session_id') || '';
  let sessionIdSource = 'none';
  if (sessionIdFromCookie) {
    sessionIdSource = 'cookie';
  } else if (sessionIdFromStorage) {
    sessionIdSource = 'localStorage';
  }

  // Validate that session_viewer token exists before making the request
  if (!viewer_token) {
    console.warn('checkLogin: session_viewer token not found in browser storage');
    showSessionError(true, 'Viewer session expired. Please try login again.');
    return null;
  }

  console.log('[AUTH-TEMP-DEBUG] /current-user-viewer request context', {
    rawTokenLength: rawViewerToken?.length || 0,
    normalizedTokenLength: viewer_token.length,
    maskedPrefix,
    hadBearerPrefix,
    hadWrappedQuotes,
    viewerTokenSource,
    sessionIdSource,
    hasSessionId: Boolean(sessionIdFromCookie || sessionIdFromStorage),
  });

  // The api instance's request interceptor attaches Authorization/x-api-key
  // from storage automatically; the guard above already validated viewer_token exists.
  try {
    const response = await api.get(path);
    return response.data;
  } catch (error) {
    if (!axios.isAxiosError(error)) {
      showSessionError(false, 'An unexpected error occurred.');
      return null;
    }

    const status = error.response?.status;

    if (!error.response) {
      showSessionError(false, 'Internal Error occurred');
      return null;
    }

    console.warn('[AUTH-TEMP-DEBUG] /current-user-viewer failed', {
      status,
      statusText: error.response.statusText,
    });

    const errorDetail = error.response.data?.detail || error.response.data?.message || '';

    if (status === 401 || status === 403) {
      // Detect if the error comes from the Session Viewer Token or standard auth token
      const isViewerTokenError = errorDetail.toLowerCase().includes('viewer');

      showSessionError(
        isViewerTokenError,
        errorDetail ||
          (isViewerTokenError ? 'Viewer Session Expired.' : 'Application Session Expired.'),
        status
      );
    } else {
      showSessionError(false, 'An unexpected error occurred.');
    }
    return null;
  }
}
